/**
 * `sessionStorage` keys that belong to a signed-in tab, and the one call that
 * clears them.
 *
 * Anything a signed-in admin keeps in the tab (recent lookups, 846) lives
 * under `SIGNED_IN_STORAGE_PREFIX`. `SessionProvider.signOut` clears them all
 * unless the sign-out is an expired session, because the same admin usually
 * signs straight back in.
 *
 * This module knows only a prefix, so no domain knowledge enters `src/lib/`
 * and the header never has to import a feature to clear its data. A store
 * that mirrors its value in memory subscribes with `onSignedInStorageCleared`
 * so it empties too.
 */

/** Every key under this prefix is removed on sign-out. */
export const SIGNED_IN_STORAGE_PREFIX = 'dawnpatrol.signedIn.';

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Removes every `sessionStorage` key with the prefix, then tells subscribers.
 * Storage that throws (sandboxed iframe, private mode) is ignored: the
 * subscribers still run, so memory fallbacks are emptied.
 */
export function clearSignedInStorage(): void {
    try {
        const storage = globalThis.sessionStorage;
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i += 1) {
            const key = storage.key(i);
            if (key?.startsWith(SIGNED_IN_STORAGE_PREFIX)) keys.push(key);
        }
        for (const key of keys) storage.removeItem(key);
    } catch {
        // Nothing to clear from, and nothing to tell the user.
    }

    for (const listener of new Set(listeners)) {
        listener();
    }
}

/** Subscribe to the cleared notice. Returns an unsubscribe function. */
export function onSignedInStorageCleared(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
